/**
 * The "Security" tab: password reset and the share code.
 *
 * Both were sections of the old detail drawer. Neither shows a secret — the reset is sent to the
 * user's own email or phone, and the share code is only visible here at the moment it is issued
 * (the admin user DTO does not carry the current one, so there is nothing to display until a
 * rotation returns the new value).
 */

import { useState } from 'react'
import { Alert, Box, Button, Paper, Stack, Typography } from '@mui/material'
import { Email, LockReset, Phone, QrCode2 } from '@mui/icons-material'
import { useMutation } from '@tanstack/react-query'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { adminApi } from '@api/endpoints/admin'
import { authApi } from '@api/endpoints/auth'
import type { AdminUserDto } from '@api/types/admin'
import { errorMessage, userLabel } from './userFormatting'

export interface UserSecurityPanelProps {
  user: AdminUserDto
}

export function UserSecurityPanel({ user }: UserSecurityPanelProps) {
  const [resetSuccess, setResetSuccess] = useState<string | null>(null)
  const [rotatedShareCode, setRotatedShareCode] = useState<string | null>(null)
  const [confirmRotateOpen, setConfirmRotateOpen] = useState(false)

  const sendPasswordReset = useMutation({
    mutationFn: (method: 'email' | 'sms') =>
      authApi.forgotPassword({
        email: method === 'email' ? user.email : undefined,
        phoneNumber: method === 'sms' ? user.phoneNumber : undefined,
        method,
      }),
    onSuccess: (_data, method) => {
      setResetSuccess(`Password reset ${method === 'email' ? 'email' : 'SMS'} sent successfully`)
    },
  })

  const rotateShareCode = useMutation({
    mutationFn: () => adminApi.rotateUserShareCode(user.id),
    onSuccess: (result) => {
      setConfirmRotateOpen(false)
      setRotatedShareCode(result.shareCode)
    },
    onError: () => setConfirmRotateOpen(false),
  })

  return (
    <Box>
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockReset fontSize="small" /> Password Reset
        </Typography>

        {sendPasswordReset.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage(sendPasswordReset.error, 'Failed to send password reset')}
          </Alert>
        )}
        {resetSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResetSuccess(null)}>
            {resetSuccess}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Sends the ordinary reset message to the address or number on the account. Nothing is
          revealed here — you cannot read the code they get.
        </Typography>

        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<Email />}
            onClick={() => sendPasswordReset.mutate('email')}
            disabled={sendPasswordReset.isPending || !user.email}
          >
            {sendPasswordReset.isPending ? 'Sending...' : 'Send Email'}
          </Button>
          {/* Only offered when there is a number to send to. */}
          {user.phoneNumber && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<Phone />}
              onClick={() => sendPasswordReset.mutate('sms')}
              disabled={sendPasswordReset.isPending}
            >
              {sendPasswordReset.isPending ? 'Sending...' : 'Send SMS'}
            </Button>
          )}
        </Stack>
      </Paper>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <QrCode2 fontSize="small" /> Share code
        </Typography>

        {rotateShareCode.error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage(rotateShareCode.error, 'Failed to rotate share code')}
          </Alert>
        )}
        {rotatedShareCode && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setRotatedShareCode(null)}>
            New share code: <strong>{rotatedShareCode}</strong>. The previous code no longer works.
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Codes issued before this was fixed were built from the user&apos;s email address, so the
          code itself disclosed it. Rotating issues a random one. The current code is not returned
          by the admin API, so it only appears here once you have issued a new one.
        </Typography>

        <Button
          variant="outlined"
          size="small"
          color="warning"
          startIcon={<QrCode2 />}
          onClick={() => setConfirmRotateOpen(true)}
          disabled={rotateShareCode.isPending}
        >
          {rotateShareCode.isPending ? 'Rotating...' : 'Issue a new share code'}
        </Button>
      </Paper>

      {/* Confirmed rather than done on the first click: rotating breaks anything already printed,
          messaged or bookmarked with the previous code, and that is not obvious from a button. */}
      <ConfirmDialog
        open={confirmRotateOpen}
        title="Issue a new share code?"
        message={`${userLabel(user)} will be given a new random share code. Their current code stops working immediately — anything already printed, messaged or bookmarked with it will no longer resolve. This cannot be undone.`}
        confirmLabel="Issue new code"
        loading={rotateShareCode.isPending}
        onConfirm={() => rotateShareCode.mutate()}
        onClose={() => setConfirmRotateOpen(false)}
      />
    </Box>
  )
}
